import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import * as complianceService from '../services/complianceService';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const {
  FiUsers,
  FiFileText,
  FiShield,
  FiTrendingUp,
  FiUserCheck,
  FiAlertTriangle,
  FiScale,
  FiSettings,
  FiFolder,
  FiLayers,
  FiUser,
  FiLogOut
} = FiIcons;

const WorkflowDashboard = () => {
  const { user, tenant, signOut } = useAuth();
  const navigate = useNavigate();
  const [tribunalAlerts, setTribunalAlerts] = useState([]);
  const [caseTypeData, setCaseTypeData] = useState([]);
  const [riskDistribution, setRiskDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [alerts, caseTypes, staffRisk] = await Promise.all([
        complianceService.getTribunalRiskAlerts(),
        complianceService.getCaseTypeCompliance(),
        complianceService.getStaffRiskDistribution()
      ]);

      setTribunalAlerts(alerts);
      setCaseTypeData(caseTypes);
      setRiskDistribution(staffRisk);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const workflowStages = [
    {
      id: 'staff-management',
      title: 'Staff Management & Overview',
      status: 'enhanced',
      icon: FiUser,
      color: 'purple',
      link: '/staff',
      description: 'Comprehensive staff directory with case tracking and performance monitoring',
      features: [
        'Complete staff directory & profiles',
        'Individual case history tracking',
        'Performance rating overview',
        'Risk level assessment',
        'Contact information management',
        'Employment status monitoring'
      ]
    },
    {
      id: 'case-management',
      title: 'Employee Relations Case Management',
      status: 'enhanced',
      icon: FiFolder,
      color: 'emerald',
      link: '/cases',
      description: 'Individual employee case tracking for disciplinary, grievance, and exit processes',
      features: [
        'Employee-specific case tracking',
        'Disciplinary & grievance processes',
        'Performance/capability management',
        'Exit/dismissal workflows',
        'Tribunal preparation tracking',
        'Settlement agreement management'
      ]
    },
    {
      id: 'company-workflows',
      title: 'Company-Level Workflow Management',
      status: 'enhanced',
      icon: FiLayers,
      color: 'blue',
      link: '/company-workflows',
      description: 'Organizational process management with step-by-step tracking',
      features: [
        'Template-based workflow creation',
        'Multi-step process tracking',
        'Department-wide initiatives',
        'Progress monitoring & reporting',
        'Automated step progression',
        'Audit trail & compliance'
      ]
    },
    {
      id: 'employee-relations',
      title: 'Employee Relations Workflows',
      status: 'detailed',
      icon: FiAlertTriangle,
      color: 'red',
      link: '/employee-relations',
      items: [
        { name: 'Informal intervention & early resolution', status: 'complete', acas: true },
        { name: 'Formal disciplinary process', status: 'complete', legal: true },
        { name: 'Grievance handling procedures', status: 'complete', legal: true },
        { name: 'Performance/capability management', status: 'complete', legal: true },
        { name: 'Long-term absence management', status: 'complete', legal: true },
        { name: 'Right to be accompanied', status: 'complete', legal: true },
        { name: 'Appeal processes', status: 'complete', legal: true }
      ]
    },
    {
      id: 'exit-tribunal',
      title: 'Exit & Tribunal Risk Management',
      status: 'detailed',
      icon: FiScale,
      color: 'orange',
      link: '/exit-processes',
      items: [
        { name: 'Fair dismissal procedures (capability, conduct, redundancy)', status: 'complete', legal: true },
        { name: 'Settlement agreement processes', status: 'complete', legal: true },
        { name: 'Tribunal preparation workflows', status: 'complete', legal: true },
        { name: 'Evidence gathering and documentation', status: 'complete', legal: true },
        { name: 'Legal compliance and risk assessment', status: 'complete', legal: true }
      ]
    },
    {
      id: 'analytics-compliance',
      title: 'Analytics & Compliance Monitoring',
      status: 'scaffold',
      icon: FiShield,
      color: 'gray',
      link: '/compliance',
      items: [
        { name: 'Case outcome analytics', status: 'pending', acas: true },
        { name: 'Tribunal risk alerts', status: 'pending', acas: true },
        { name: 'Compliance monitoring dashboard', status: 'pending', legal: true },
        { name: 'Employee relations trends', status: 'pending', acas: true }
      ]
    }
  ];

  const getStageColor = (color) => {
    const colors = {
      purple: 'bg-purple-500',
      emerald: 'bg-emerald-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      indigo: 'bg-indigo-500',
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      gray: 'bg-gray-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Welcome Header - Compact */}
      <div className="mb-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-3 md:p-4 text-white">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0">
          <div>
            <h1 className="text-base md:text-xl font-bold">
              Welcome back, {user?.user_metadata?.full_name || user?.email}
            </h1>
            <p className="text-blue-100 text-xs md:text-sm">
              {tenant ? `Managing ${tenant.name}` : 'UK HR Compliance System'}
            </p>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-2 rounded-lg transition-colors text-xs md:text-sm"
          >
            <SafeIcon icon={FiLogOut} />
            <span className="hidden md:inline">Sign Out</span>
            <span className="md:hidden">Logout</span>
          </button>
        </div>
      </div>

      {/* Tribunal Risk Alert Panel - Compact Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 bg-white rounded-lg shadow-lg overflow-hidden border-l-4 border-red-500"
      >
        <div className="bg-red-50 px-6 py-3 border-b border-red-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-2xl" />
              <div>
                <h3 className="text-lg font-bold text-red-900">TRIBUNAL RISK ALERTS</h3>
                <p className="text-red-700 text-sm">Urgent compliance issues requiring immediate attention</p>
              </div>
            </div>
            {!loading && tribunalAlerts.length > 0 && (
              <button
                onClick={() => setShowAlertsModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                View All ({tribunalAlerts.length})
              </button>
            )}
          </div>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading alerts...</p>
            </div>
          ) : tribunalAlerts.length === 0 ? (
            <div className="text-center py-8">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500 text-4xl mx-auto mb-2" />
              <p className="text-gray-600">No urgent tribunal risks detected</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Urgent Alerts */}
              {(() => {
                const urgentAlerts = tribunalAlerts.filter(a => a.severity === 'urgent');
                const hasAlerts = urgentAlerts.length > 0;
                return (
                  <button
                    onClick={() => {
                      if (hasAlerts) {
                        setSelectedSeverity('urgent');
                        setShowAlertsModal(true);
                      }
                    }}
                    disabled={!hasAlerts}
                    className={`rounded-lg p-4 text-left transition-colors ${hasAlerts
                      ? 'bg-red-50 border-2 border-red-200 hover:bg-red-100 cursor-pointer'
                      : 'bg-gray-50 border-2 border-gray-200 cursor-default'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">{hasAlerts ? '🔴' : '✅'}</span>
                      <span className={`text-2xl font-bold ${hasAlerts ? 'text-red-600' : 'text-gray-400'}`}>
                        {urgentAlerts.length}
                      </span>
                    </div>
                    <h4 className={`font-bold text-sm uppercase mb-1 ${hasAlerts ? 'text-red-900' : 'text-gray-600'}`}>
                      Urgent
                    </h4>
                    <p className={`text-xs ${hasAlerts ? 'text-red-700' : 'text-gray-500'}`}>
                      {hasAlerts ? 'Incomplete Legal Actions' : 'No incomplete legal actions'}
                    </p>
                  </button>
                );
              })()}

              {/* Critical Alerts */}
              {(() => {
                const criticalAlerts = tribunalAlerts.filter(a => a.severity === 'critical');
                const hasAlerts = criticalAlerts.length > 0;
                return (
                  <button
                    onClick={() => {
                      if (hasAlerts) {
                        setSelectedSeverity('critical');
                        setShowAlertsModal(true);
                      }
                    }}
                    disabled={!hasAlerts}
                    className={`rounded-lg p-4 text-left transition-colors ${hasAlerts
                      ? 'bg-orange-50 border-2 border-orange-200 hover:bg-orange-100 cursor-pointer'
                      : 'bg-gray-50 border-2 border-gray-200 cursor-default'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">{hasAlerts ? '🟠' : '✅'}</span>
                      <span className={`text-2xl font-bold ${hasAlerts ? 'text-orange-600' : 'text-gray-400'}`}>
                        {criticalAlerts.length}
                      </span>
                    </div>
                    <h4 className={`font-bold text-sm uppercase mb-1 ${hasAlerts ? 'text-orange-900' : 'text-gray-600'}`}>
                      Critical
                    </h4>
                    <p className={`text-xs ${hasAlerts ? 'text-orange-700' : 'text-gray-500'}`}>
                      {hasAlerts ? 'ACAS Actions Overdue' : 'No ACAS actions overdue'}
                    </p>
                  </button>
                );
              })()}

              {/* Warning Alerts */}
              {(() => {
                const warningAlerts = tribunalAlerts.filter(a => a.severity === 'warning');
                const hasAlerts = warningAlerts.length > 0;
                return (
                  <button
                    onClick={() => {
                      if (hasAlerts) {
                        setSelectedSeverity('warning');
                        setShowAlertsModal(true);
                      }
                    }}
                    disabled={!hasAlerts}
                    className={`rounded-lg p-4 text-left transition-colors ${hasAlerts
                      ? 'bg-yellow-50 border-2 border-yellow-200 hover:bg-yellow-100 cursor-pointer'
                      : 'bg-gray-50 border-2 border-gray-200 cursor-default'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">{hasAlerts ? '🟡' : '✅'}</span>
                      <span className={`text-2xl font-bold ${hasAlerts ? 'text-yellow-600' : 'text-gray-400'}`}>
                        {warningAlerts.length}
                      </span>
                    </div>
                    <h4 className={`font-bold text-sm uppercase mb-1 ${hasAlerts ? 'text-yellow-900' : 'text-gray-600'}`}>
                      Warning
                    </h4>
                    <p className={`text-xs ${hasAlerts ? 'text-yellow-700' : 'text-gray-500'}`}>
                      {hasAlerts ? 'Cases >90 Days' : 'No cases over 90 days'}
                    </p>
                  </button>
                );
              })()}
            </div>
          )}
        </div>
      </motion.div>

      {/* Alerts Modal */}
      {showAlertsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            <div className="bg-red-50 px-6 py-4 border-b border-red-100 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-2xl" />
                <div>
                  <h3 className="text-lg font-bold text-red-900">
                    {selectedSeverity ? `${selectedSeverity.toUpperCase()} Alerts` : 'All Tribunal Risk Alerts'}
                  </h3>
                  <p className="text-red-700 text-sm">
                    {selectedSeverity
                      ? tribunalAlerts.filter(a => a.severity === selectedSeverity).length
                      : tribunalAlerts.length} alert{(selectedSeverity ? tribunalAlerts.filter(a => a.severity === selectedSeverity).length : tribunalAlerts.length) !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAlertsModal(false);
                  setSelectedSeverity(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                {(selectedSeverity
                  ? tribunalAlerts.filter(a => a.severity === selectedSeverity)
                  : tribunalAlerts
                ).map((alert, index) => (
                  <div
                    key={index}
                    className={`border-l-4 p-4 rounded-r-lg ${alert.severity === 'urgent'
                      ? 'border-red-500 bg-red-50'
                      : alert.severity === 'critical'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-yellow-500 bg-yellow-50'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span
                            className={`text-lg ${alert.severity === 'urgent'
                              ? 'text-red-600'
                              : alert.severity === 'critical'
                                ? 'text-orange-600'
                                : 'text-yellow-600'
                              }`}
                          >
                            {alert.severity === 'urgent' ? '🔴' : alert.severity === 'critical' ? '🟠' : '🟡'}
                          </span>
                          <span
                            className={`font-bold uppercase text-sm ${alert.severity === 'urgent'
                              ? 'text-red-900'
                              : alert.severity === 'critical'
                                ? 'text-orange-900'
                                : 'text-yellow-900'
                              }`}
                          >
                            {alert.severity}: {alert.title}
                          </span>
                        </div>
                        <div className="ml-7 space-y-1">
                          <p className="text-gray-900 font-medium">
                            {alert.case_type?.replace('-', ' ')} case ({alert.staff_name})
                          </p>
                          <p className="text-gray-700 text-sm">Action: {alert.description}</p>
                          {alert.days_overdue > 0 && (
                            <p className="text-red-600 text-sm font-semibold">
                              Overdue: {alert.days_overdue} days
                            </p>
                          )}
                        </div>
                      </div>
                      <Link
                        to={`/case/${alert.case_id}`}
                        onClick={() => setShowAlertsModal(false)}
                        className="ml-4 px-3 py-1 bg-white border border-gray-300 rounded text-sm font-medium hover:bg-gray-50"
                      >
                        View Case
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          UK HR Compliance System
        </h2>
        <p className="text-gray-600">
          Comprehensive staff management with employee relations case tracking and organizational workflow management
        </p>
      </div>

      {/* Featured Systems */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Staff Management System */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="px-8 py-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <SafeIcon icon={FiUser} className="text-3xl" />
                <div>
                  <h2 className="text-xl font-bold">Staff Management</h2>
                  <p className="text-purple-100 text-sm">
                    Complete staff directory & case tracking
                  </p>
                </div>
              </div>
              <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm font-medium">
                STAFF-FOCUSED
              </span>
            </div>
            <ul className="space-y-2 text-purple-100 text-sm mb-6">
              <li>• Comprehensive staff profiles & contact info</li>
              <li>• Individual case history tracking</li>
              <li>• Performance & risk level monitoring</li>
              <li>• Employment status management</li>
            </ul>
            <Link
              to="/staff"
              className="inline-flex items-center bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
            >
              <SafeIcon icon={FiUser} className="mr-2" />
              Manage Staff Directory
              <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" />
            </Link>
          </div>
        </motion.div>

        {/* Case Management System */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="px-8 py-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <SafeIcon icon={FiFolder} className="text-3xl" />
                <div>
                  <h2 className="text-xl font-bold">Employee Relations Cases</h2>
                  <p className="text-emerald-100 text-sm">
                    Individual employee case tracking & tribunal risk management
                  </p>
                </div>
              </div>
              <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm font-medium">
                CASE-BASED
              </span>
            </div>
            <ul className="space-y-2 text-emerald-100 text-sm mb-6">
              <li>• Disciplinary & grievance case management</li>
              <li>• Performance/capability tracking</li>
              <li>• Exit processes & dismissal procedures</li>
              <li>• Settlement agreements & tribunal prep</li>
            </ul>
            <Link
              to="/cases"
              className="inline-flex items-center bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
            >
              <SafeIcon icon={FiFolder} className="mr-2" />
              Manage Employee Cases
              <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" />
            </Link>
          </div>
        </motion.div>

        {/* Company Workflow System */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="px-8 py-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <SafeIcon icon={FiLayers} className="text-3xl" />
                <div>
                  <h2 className="text-xl font-bold">Company Workflows</h2>
                  <p className="text-blue-100 text-sm">
                    Organizational process management & department initiatives
                  </p>
                </div>
              </div>
              <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm font-medium">
                PROCESS-BASED
              </span>
            </div>
            <ul className="space-y-2 text-blue-100 text-sm mb-6">
              <li>• Recruitment campaigns & hiring processes</li>
              <li>• Onboarding programs & training rollouts</li>
              <li>• Performance review cycles</li>
              <li>• Policy implementation & compliance audits</li>
            </ul>
            <Link
              to="/company-workflows"
              className="inline-flex items-center bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              <SafeIcon icon={FiLayers} className="mr-2" />
              Manage Company Workflows
              <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Risk Visualizations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Risk Heatmap by Case Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiAlertTriangle} className="text-white text-2xl" />
              <div>
                <h3 className="text-white font-semibold text-lg">Risk Heatmap by Case Type</h3>
                <p className="text-red-100 text-sm">Tribunal risk intensity across case categories</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {caseTypeData.slice(0, 6).map((caseType, index) => {
                  const score = caseType.compliance_score || 0;
                  const riskLevel = score >= 80 ? 'low' : score >= 60 ? 'medium' : 'high';
                  return (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900 capitalize">
                            {caseType.case_type?.replace(/-/g, ' ') || 'Unknown'}
                          </span>
                          <span className={`text-sm font-bold px-2 py-1 rounded ${riskLevel === 'high' ? 'bg-red-100 text-red-700' :
                            riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                            {riskLevel.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${riskLevel === 'high' ? 'bg-red-500' :
                                riskLevel === 'medium' ? 'bg-yellow-500' :
                                  'bg-green-500'
                                }`}
                              style={{ width: `${100 - score}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 w-16 text-right">
                            {caseType.active_cases || 0} cases
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>

        {/* Donut Chart - Risk Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-purple-500 to-indigo-500 px-6 py-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiShield} className="text-white text-2xl" />
              <div>
                <h3 className="text-white font-semibold text-lg">Staff Risk Distribution</h3>
                <p className="text-purple-100 text-sm">Overall tribunal risk breakdown</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={riskDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      style={{ fontSize: '14px' }}
                    >
                      {riskDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: '14px' }} />
                    <Legend wrapperStyle={{ fontSize: '14px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  {riskDistribution.map((item, index) => (
                    <div key={index}>
                      <p className="text-2xl font-bold" style={{ color: item.color }}>
                        {item.value}
                      </p>
                      <p className="text-xs text-gray-600">{item.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Reference Workflows Section */}
      <div className="mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="bg-indigo-500 px-6 py-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiFileText} className="text-white text-2xl" />
              <div>
                <h3 className="text-white font-semibold text-lg">Reference Workflow Templates</h3>
                <p className="text-indigo-100 text-sm">Detailed process guides and policy frameworks</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-gray-600 mb-4">
              Access detailed workflow templates and compliance guides for organizational processes:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: 'Recruitment Process', link: '/recruitment', icon: FiUsers, color: 'blue' },
                { name: 'Onboarding/Induction', link: '/onboarding', icon: FiUserCheck, color: 'green' },
                { name: 'Core Employee Lifecycle', link: '/core-lifecycle', icon: FiSettings, color: 'purple' },
                { name: 'Performance Management', link: '/performance', icon: FiTrendingUp, color: 'indigo' }
              ].map((workflow, index) => (
                <Link
                  key={index}
                  to={workflow.link}
                  className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon
                    icon={workflow.icon}
                    className={`text-xl ${getStageColor(workflow.color).replace('bg-', 'text-')}`}
                  />
                  <span className="font-medium text-gray-900">{workflow.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Employee Relations & Exit Workflows */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {workflowStages.slice(3).map((stage) => (
          <motion.div
            key={stage.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`${getStageColor(stage.color)} px-6 py-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={stage.icon} className="text-white text-2xl" />
                  <h3 className="text-white font-semibold text-lg">
                    {stage.title}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  {stage.status === 'detailed' && (
                    <span className="bg-white bg-opacity-20 text-white px-2 py-1 rounded text-sm font-medium">
                      Fully Detailed
                    </span>
                  )}
                  {stage.status === 'scaffold' && (
                    <span className="bg-white bg-opacity-20 text-white px-2 py-1 rounded text-sm font-medium">
                      High-Level Scaffold
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {stage.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-3 h-3 rounded-full ${item.status === 'complete'
                          ? 'bg-green-500'
                          : item.status === 'pending'
                            ? 'bg-yellow-500'
                            : 'bg-gray-300'
                          }`}
                      />
                      <span className="text-gray-900 font-medium">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {item.legal && (
                        <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">
                          ⚖️ Legal
                        </span>
                      )}
                      {item.acas && (
                        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">
                          🟢 ACAS
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {stage.link && (
                <div className="mt-6">
                  <Link
                    to={stage.link}
                    className={`inline-flex items-center px-4 py-2 ${getStageColor(
                      stage.color
                    )} text-white rounded-lg hover:opacity-90 transition-opacity`}
                  >
                    <span>View Detailed Workflow</span>
                    <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" />
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-blue-600 text-xl mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">System Architecture</h4>
            <div className="text-blue-800 space-y-1">
              <p>
                <strong>👥 Staff Management:</strong> Comprehensive staff directory with individual profiles and case
                history tracking
              </p>
              <p>
                <strong>📁 Case Management:</strong> Individual employee issues requiring formal tracking and tribunal
                defense
              </p>
              <p>
                <strong>🔄 Company Workflows:</strong> Organizational processes with structured steps and progress
                tracking
              </p>
              <p>
                <strong>📚 Reference Templates:</strong> Detailed compliance guides and policy frameworks
              </p>
              <p>
                <strong>⚖️ Legal Compliance:</strong> Full audit trails and statutory obligation tracking
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDashboard;